import React from 'react';
import PropTypes from 'prop-types';
import style from '@src/js/Style';
import Card from '../Base/Card';
import NumberFormatter from '../../NumberFormatter';
import Interface from '../Site/Interface';
import InterfaceStorage from '../Site/InterfaceStorage';
import { Doughnut } from 'react-chartjs-2';
import Api from '../../Api';

export default class PaymentDetails extends React.Component {

    static DAYS_IN_MONTH = 30;

    constructor(props) {

        super(props);

        this.nodes = {
            chart: React.createRef(),
        };

        this.state = {

            // regular block
            payment: null, // number
            daily: null, // number
            dailyTemplate: null, // text,
            dailyText: null, // text
            visualization: null, // number
            visualizationTemplate: null, // text,
            visualizationText: null, // text
            creditBodyPayment: null, // number
            overpayment: null, // number

            // chart block
            sumCreditBodyPayment: null, // number
            sumOverpayment: null, // number
            sumPayment: null, // number

        };
    }

    static get propTypes() {

        return {

            /**
             * @var {object} currency
             * symbol and iso required
             */
            currency: PropTypes.object.isRequired,

            /**
             * @var {integer} countryId
             * current country id
             */
            countryId: PropTypes.number.isRequired,
        };
    }

    componentDidMount() {

        const templates = Interface.get(InterfaceStorage.COUNTRY, 'regularPaymentTemplates', true, false);
        if (templates) {
            
            this.setState({
                dailyTemplate: templates.daily,
                visualizationTemplate: templates.visualization,
            });

            Api
                .fetch(`${Api.origin}/api/v2/price/offer.json?country=${this.props.countryId}&slug=A+cup+of+coffee`)
                .then((response) => {

                    if (response.avg == undefined)
                        console.error(`Can't get price coffee by ${this.props.countryId}`);
    
                    const
                        avg = parseFloat(response.avg ?? /** incorrect coffee price*/ 100),
                        visualization = NumberFormatter
                            .parseEx(avg, { currency: this.props.currency })
                            .round();

                    this.setState({ visualization });
                })
                .catch(() => {});
        }
    }

    setState(newState) {

        // before render action

        const combinedState = {

            dailyTemplate: newState.dailyTemplate ?? this.state.dailyTemplate,
            daily: newState.daily ?? this.state.daily,

            visualizationTemplate: newState.visualizationTemplate ?? this.state.visualizationTemplate,
            visualization: newState.visualization ?? this.state.visualization,
        };

        if (combinedState.dailyTemplate && combinedState.daily)
            newState.dailyText = combinedState.dailyTemplate.replace('[value]', combinedState.daily.formatted());

        if (combinedState.visualizationTemplate && combinedState.visualization && combinedState.daily) {

            const visualizationCount = combinedState.daily
                .clone()
                .div(combinedState.visualization)
                .round()
                .toInt();
            
            newState.visualizationText = combinedState.visualizationTemplate.replace('[value]', visualizationCount);
        }
            
        super.setState(newState);
    }

    componentDidUpdate() {

        this.nodes.chart.current.data = this.getChartData();
        this.nodes.chart.current.update('none');
    }

    //------------------------------------------------------------------------------------------------------------------
    // RENDER
    //------------------------------------------------------------------------------------------------------------------

    render() {

        return (
            <div className={ style('flex gap mobile:flex-wrap tablet:flex-wrap justify-between my-8 text-small') }>
                { this.renderRegularBlock() }
                { this.renderChartBlock() }
            </div>
        );
    }

    renderRegularBlock() {

        const state = this.state;

        return (
            <Card className={ style('w-40 tablet:w-full mobile:w-full text-dark') }>

                {/* title */}
                <Interface
                    className={ style('w-full title3 mb-1') }
                    storage={ InterfaceStorage.COUNTRY }
                    id='regularPaymentTitle' />

                {/* subtitle */}
                <Interface
                    className={ style('w-full text-muted-alt') }
                    storage={ InterfaceStorage.COUNTRY }
                    id='regularPaymentText' />

                {/* payment, daily and visualization */}
                <div className={ style('my-6 flex-center') }>

                    {/* payment */}
                    <div className={ style('title2 w-50 bg-primary-light rounded p-4 text-center') }>
                        { state.payment?.formatted()  }
                    </div>

                    <div className={ style('w-50 flex flex-wrap p-4 gap-md') }>

                        {/* daily payment */}
                        <div className={ style('w-full text-muted-alt') }>
                            { this.state.dailyText }
                        </div>

                        {/* visualization */}
                        <div className={ style('flex-center gap-0') }>
                            <svg className={ style('size-20 mr-3') } width='20' height='19' viewBox='0 0 20 19' fill='none' xmlns='http://www.w3.org/2000/svg'>
                                <path d='M7 14.5293H11C12.3261 14.5293 13.5979 14.0025 14.5355 13.0648C15.4732 12.1271 16 10.8554 16 9.5293V8.5293H17C17.7956 8.5293 18.5587 8.21323 19.1213 7.65062C19.6839 7.08801 20 6.32495 20 5.5293C20 4.73365 19.6839 3.97059 19.1213 3.40798C18.5587 2.84537 17.7956 2.5293 17 2.5293H16V1.5293C16 1.26408 15.8946 1.00973 15.7071 0.82219C15.5196 0.634654 15.2652 0.529297 15 0.529297H3C2.73478 0.529297 2.48043 0.634654 2.29289 0.82219C2.10536 1.00973 2 1.26408 2 1.5293V9.5293C2 10.8554 2.52678 12.1271 3.46447 13.0648C4.40215 14.0025 5.67392 14.5293 7 14.5293ZM16 4.5293H17C17.2652 4.5293 17.5196 4.63465 17.7071 4.82219C17.8946 5.00973 18 5.26408 18 5.5293C18 5.79451 17.8946 6.04887 17.7071 6.2364C17.5196 6.42394 17.2652 6.5293 17 6.5293H16V4.5293ZM4 2.5293H14V9.5293C14 10.3249 13.6839 11.088 13.1213 11.6506C12.5587 12.2132 11.7956 12.5293 11 12.5293H7C6.20435 12.5293 5.44129 12.2132 4.87868 11.6506C4.31607 11.088 4 10.3249 4 9.5293V2.5293ZM19 16.5293H1C0.734784 16.5293 0.48043 16.6347 0.292893 16.8222C0.105357 17.0097 0 17.2641 0 17.5293C0 17.7945 0.105357 18.0489 0.292893 18.2364C0.48043 18.4239 0.734784 18.5293 1 18.5293H19C19.2652 18.5293 19.5196 18.4239 19.7071 18.2364C19.8946 18.0489 20 17.7945 20 17.5293C20 17.2641 19.8946 17.0097 19.7071 16.8222C19.5196 16.6347 19.2652 16.5293 19 16.5293Z' fill='#1373E5'/>
                            </svg>
                            <div className={ style('text-medium') }>
                                { this.state.visualizationText }
                            </div>
                        </div>
                    </div>

                </div>

                <div className={ style('flex flex-wrap w-full mt-8 text-medium') }>

                    {/* credit body payment */}
                    <div className={ style('flex w-full justify-between') }>
                        <Interface storage={ InterfaceStorage.COUNTRY } id='regularPaymentSum' />
                        <div>{ state.creditBodyPayment?.formatted() }</div>
                    </div>

                    <hr className={ style('linebreak my-4') } />

                    {/* overpayment */}
                    <div className={ style('flex w-full justify-between') }>
                        <Interface storage={ InterfaceStorage.COUNTRY } id='regularOverpaymentSum' />
                        <div>{ state.overpayment?.formatted() }</div>
                    </div>

                </div>

            </Card>
        );
    }

    renderChartBlock() {

        return (
            <Card className={ style('flex-1') }>
                
                {/* title */}
                <Interface
                    className={ style('w-full title3 mb-1') }
                    storage={ InterfaceStorage.COUNTRY }
                    id='chartPaymentTitle' />

                {/* subtitle */}
                <Interface
                    className={ style('w-full text-muted-alt') }
                    storage={ InterfaceStorage.COUNTRY }
                    id='chartPaymentText' />
                
                <div className={ style('flex w-full gap-lg mt-6 mobile:flex-wrap') }>
                    
                    {/* chart */}
                    <div className={ style('size-200 relative') }>
                        <Doughnut
                            ref={ this.nodes.chart }
                            style={{ position: 'relative', zIndex: 1000 }}
                            options={ this.getChartOptions() }
                            data={ this.getChartData() } />
                    </div>
                    
                    {/* sum */}
                    { this.renderChartSumPart() }

                </div>

            </Card>
        );
    }

    renderChartSumPart() {

        const details = this.getSumDetails();

        return (
            
            <div className={ style('flex-1 flex flex-wrap gap-md text-muted-alt text-small') }>

                {/* credit body */}
                <div className={ style('flex w-full justify-between') }>
                    <div className={ style('flex-center gap-md') }>
                        <div
                            className={ style('size-16 rounded-full aspect-square') }
                            style={{ background: details.creditBody.color }} />
                        <Interface
                            storage={ InterfaceStorage.COUNTRY }
                            id='chartSumWithoutOverpaymentLabel' />
                    </div>
                    <div className={ style('text-bold') }>
                        { details.creditBody.value?.formatted() }
                    </div>
                </div>
                
                <hr className={ style('linebreak my-4') } />

                {/* overpayment */}
                <div className={ style('flex w-full justify-between') }>
                    <div className={ style('flex-center gap-md') }>
                        <div
                            className={ style('size-16 rounded-full aspect-square') }
                            style={{ background: details.overpayment.color }} />
                        <Interface
                            storage={ InterfaceStorage.COUNTRY }
                            id='chartSumOverpaymentLabel' />
                    </div>
                    <div className={ style('text-bold') }>
                        { details.overpayment.value?.formatted() }
                    </div>
                </div>

                <hr className={ style('linebreak my-4') } />

                {/* sum */}
                <div className={ style('flex w-full justify-between text-bold') }>
                    <Interface
                        storage={ InterfaceStorage.COUNTRY }
                        id='chartSumPaymentLabel' />
                    <div>
                        { this.state.sumPayment?.formatted() }
                    </div>
                </div>

            </div>
        );
    }

    //------------------------------------------------------------------------------------------------------------------
    // GETTERS, SETTERS
    //------------------------------------------------------------------------------------------------------------------
    
    getChartOptions() {

        return {
            animation: {
                duration: 0,
            },
        };
    }

    getChartData() {
        
        const

            data = this.getSumDetails(),

            chartData = {
                datasets: [
                    {
                        data: [],
                        backgroundColor: [],
                    },
                ],
            };

        Object
            .values(data)
            .forEach((item) => {

                chartData.datasets[0].data.push(item.value?.toFloat());
                chartData.datasets[0].backgroundColor.push(item.color);
            });

        return chartData;
    }

    getSumDetails() {

        return {
            creditBody: {color: '#1373E5', value: this.state.sumCreditBodyPayment},
            overpayment: {color: '#9556D3', value: this.state.sumOverpayment},
        };
    }
    
    //------------------------------------------------------------------------------------------------------------------
    // ACTIONS
    //------------------------------------------------------------------------------------------------------------------

    update(report) {

        const newState = {};

        // regular block
        newState.payment = NumberFormatter.parseEx(report.details.avgPayment, { currency: this.props.currency });
        newState.daily = newState.payment.clone().div(PaymentDetails.DAYS_IN_MONTH).round();
        newState.overpayment = NumberFormatter.parseEx(report.details.avgOverpayment, { currency: this.props.currency });
        newState.creditBodyPayment = newState.payment.clone().minus(newState.overpayment);

        // chart block
        newState.sumOverpayment = NumberFormatter.parseEx(report.details.sumOverpayment, { currency: this.props.currency });
        newState.sumPayment = NumberFormatter.parseEx(report.details.sumPayment, { currency: this.props.currency });
        newState.sumCreditBodyPayment = newState.sumPayment.clone().minus(newState.sumOverpayment);
        // @todo: use sumWithoutOverpayment param

        this.setState(newState);
    }

}